"use client";

import { useRef, useState } from "react";

import IconWriting from "@ui/central-icons/icons/IconWriting";
import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import { formatTime } from "@ui/lib/time";
import { cn } from "@ui/lib/utils";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { useTranslations } from "next-intl";

import { Button } from "../core/Button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../core/Tooltip";
import { AudioWidgetPill } from "./AudioWidgetPill";
import { useAudioPlayer } from "./AudioPlayerContext";

interface AudioPlayerBarProps {
  title: string;
  onEditPrompt?: () => void;
  onDownload?: () => void;
  className?: string;
  showProgressBar?: boolean;
  seekable?: boolean;
}

function AudioSeekBar({
  currentTime,
  duration,
  onSeek,
  seekable = true,
}: {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  seekable?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const progress = duration > 0 ? currentTime / duration : 0;

  const timeFromPointer = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * duration;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    onSeek(timeFromPointer(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    onSeek(timeFromPointer(e.clientX));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div
      ref={trackRef}
      role={seekable ? "slider" : "progressbar"}
      aria-valuemin={0}
      aria-valuemax={duration}
      aria-valuenow={currentTime}
      tabIndex={seekable ? 0 : undefined}
      className={
        seekable
          ? "tw-group tw-relative tw-flex tw-flex-1 tw-touch-none tw-select-none tw-cursor-pointer tw-items-center tw-py-2 tw-outline-none focus-visible:tw-outline-focus"
          : "tw-relative tw-flex tw-flex-1 tw-items-center tw-py-2"
      }
      onPointerDown={seekable ? handlePointerDown : undefined}
      onPointerMove={seekable ? handlePointerMove : undefined}
      onPointerUp={seekable ? handlePointerUp : undefined}
      onKeyDown={
        seekable
          ? e => {
              if (e.key === "ArrowRight") onSeek(Math.min(currentTime + 5, duration));
              if (e.key === "ArrowLeft") onSeek(Math.max(currentTime - 5, 0));
            }
          : undefined
      }
    >
      {/* Track */}
      <div className='tw-relative tw-h-0.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-black/10'>
        <div className='tw-absolute tw-h-full tw-rounded-full tw-bg-black' style={{ width: `${progress * 100}%` }} />
      </div>
      {/* Thumb — only shown when seekable */}
      {seekable && (
        <div
          className='tw-pointer-events-none tw-absolute tw-size-2.5 tw-rounded-full tw-bg-black tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100 focus-visible:tw-opacity-100'
          style={{ left: `calc(${progress * 100}% - 5px)` }}
        />
      )}
    </div>
  );
}

const AudioPlayerBar = ({
  title,
  onEditPrompt,
  onDownload,
  className,
  showProgressBar = false,
  seekable = true,
}: AudioPlayerBarProps) => {
  const t = useTranslations("ClientKeys.AudioPlayerBar");
  const { playState, currentTime, duration, togglePlayPause, seek } = useAudioPlayer();
  const isPlaying = playState === "playing";

  return (
    <AudioWidgetPill
      className={cn(
        "tw-w-full tw-max-w-[300px] md:tw-w-auto md:tw-max-w-[420px] tw-pl-[0.625rem] tw-pr-3 tw-py-2.5 tw-overflow-hidden",
        showProgressBar && "md:tw-min-w-[480px] md:tw-max-w-[680px]",
        className,
      )}
    >
      {/* Play/Pause */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='elevated'
              size='pill'
              className='tw-shrink-0 tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]'
              aria-label={isPlaying ? t("pause") : t("play")}
              icon={
                isPlaying ? (
                  <IconPauseFill className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />
                ) : (
                  <IconPlayFill className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />
                )
              }
              onClick={togglePlayPause}
            />
          </TooltipTrigger>
          <TooltipContent variant='dark-new'>{isPlaying ? t("pause") : t("play")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Title + time/progress */}
      <div className='tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-1 tw-justify-center'>
        <p className='tw-type-base tw-pb-1 tw-pt-0.5 tw-text-black'>
          <span className='tw-block tw-truncate tw-whitespace-nowrap'>{title}</span>
        </p>
        {showProgressBar ? (
          <div className='tw-flex tw-items-center tw-gap-2'>
            <span className='tw-type-sm tw-tabular-nums tw-text-gray-500 tw-shrink-0'>{formatTime(currentTime)}</span>
            <AudioSeekBar currentTime={currentTime} duration={duration} onSeek={seek} seekable={seekable} />
            <span className='tw-type-sm tw-tabular-nums tw-text-gray-500 tw-shrink-0'>{formatTime(duration)}</span>
          </div>
        ) : (
          <p className='tw-type-sm tw-pb-0.5 tw-tabular-nums tw-text-gray-500'>
            {formatTime(currentTime)} / {formatTime(duration)}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className='tw-flex tw-shrink-0 tw-items-center tw-rounded-lg'>
        {onEditPrompt && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost-subtle'
                  size='pill-md'
                  className='tw-opacity-60'
                  aria-label={t("edit_prompt")}
                  icon={<IconWriting className={cn(iconStrokeFix, "tw-size-6")} />}
                  onClick={onEditPrompt}
                />
              </TooltipTrigger>
              <TooltipContent variant='dark-new'>{t("edit_prompt")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost-subtle'
                size='pill-md'
                className='tw-opacity-60'
                aria-label={t("download")}
                icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-6")} />}
                onClick={onDownload}
              />
            </TooltipTrigger>
            <TooltipContent variant='dark-new'>{t("download")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </AudioWidgetPill>
  );
};

AudioPlayerBar.displayName = "AudioPlayerBar";

export { AudioPlayerBar };
export type { AudioPlayerBarProps };
